import { request, api } from '@/hnx/hnx';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import dayjs from 'dayjs';

api.$register('core', {
	dictionary: (name, shortForm) => request.get(`/Dictionary/${name}${shortForm ? '?short' : ''}`),
	getWeather: () => request.get('/Weather/Get'),
	navbarSearch: query => request.get(`/Common/Home/Search/${query}`),
	getAppVersion: () => request.get(`/Version?nocache=${Date.now()}`),
	appConfig: () => request.get('/AppConfig'),
});

api.$register('user', {
	login: (login, password) => request.post('/Users/Login', {login, password}),
	logout: () => request.post('/Users/Logout', {}),
	current: () => request.get('/Users/Login'),
	sessionPing: () => request.post('/Session/Ping', {}),
	passwordChange: data => request.post('/Users/Password/Change', data),
	profile: {
		get: () => request.get('/Users/Profile'),
		update: data => request.post('/Users/Profile', data)
	}
});

api.$register('employees', {
	list: () => request.get('/Employees/List'),
	get: login => request.get(`/Employees/Details/${login}`),
	create: data => request.put('/Employees/Details', data),
	update: data => request.post(`/Employees/Details/${data.login}`, data),
	remove: login => request.delete(`/Employees/Details/${login}`),
	instructorLessons: login => request.get(`/Employees/Lessons/${login}`),
	resetUserPassword: login => request.post(`/Employees/RegeneratePassword/${login}`),
	settlement: {
		get: instructor => request.get(`Employees/InstructorSettlement/${instructor}`),
		entry: {
			create: (instructor, entry) => request.put(`/Employees/InstructorSettlement/${instructor}`, entry),
			update: (instructor, entry) => request.post(`/Employees/InstructorSettlement/${instructor}`, entry),
			remove: (instructor, entryId) => request.delete(`/Employees/InstructorSettlement/${instructor}?entryId=${entryId}`)
		}
	}
});

api.$register('equipment', {
	list: () => request.get('/Equipment/List'),
	get: id => request.get(`/Equipment/Details/${id}`),
	create: data => request.put('/Equipment/Details', data),
	update: data => request.post(`/Equipment/Details/${data.id}`, data),
	remove: id => request.delete(`/Equipment/Details/${id}`),
	categories: {
		list: () => request.get('/Equipment/Categories'),
		create: data => request.put('/Equipment/Categories', data),
		update: data => request.post(`/Equipment/Categories/${data.id}`, data),
		remove: id => request.delete(`/Equipment/Categories/${id}`)
	}
});

api.$register('history', {
	getAll: () => request.get('/History/Get?mode=all'),
	getLessonDate: lessonId => request.get(`/History/LessonDate/${lessonId}`)
});

api.$register('instructors', {
	lessons: (year, month) => request.get(`/Instructors/Lessons/${year}${isUndefined(month) || isNull(month) ? '' : `/${month}`}`),
	summary: (year, month) => request.get(`/Instructors/Summary/${year}/${month}`)
});

api.$register('planning', {
	getPlan: date => request.get(`/v2/Planning/Data/${date}`),
	savePlan: (date, plan) => request.post(`/v2/Planning/Data/${date}`, {plan}),
	copyPlan: (from, to) => request.post('/Planning/CopyPlan', {from, to}),
	publicPlanUrl: () => request.url('/Planning/Data/Stream'),
	lesson: {
		cancelAll: (date, data) => request.post(`/Planning/CancelAllLessons/${date}`, data),
		removeAll: date => request.post(`/Planning/RemoveAllLessons/${date}`),
		reminder: (date, lessonId, message) => request.post(`/Planning/LessonReminder/${date}${lessonId ? `/${lessonId}` : ''}`, {message})
	},
	search: {
		trainees: (from, to = from, allTrainees = 'false') => request.get('/Planning/Search/Trainees', {from, to, allTrainees}),
		traineeLevel: traineeId => request.get(`/Planning/TraineeLevel/${traineeId}`)
	},
	downloadDayPdf: date => window.location.href = `${request.$config.baseURL}/Planning/ExportToPDF/${date}`
});

api.$register('stats', {
	get: (from, to) => request.get(`/Stats/Get/${from}/${to}`),
	list: (entries, from, to) => request.get(`/Stats/List/${entries}/${from}/${to}`)
});

api.$register('trainees', {
	list: () => request.get('/Trainees/List'),
	contactInfo: (from = '1900-01-01', to = '9999-12-31') => request.get(`/Trainees/ContactInfo?from=${from}&to=${to}`),
	sendTextMessage: (message = '', trainees = []) => request.post('/Trainees/SendTextMessage', {message, trainees}),
	payments: (from, to) => request.get(`/Trainees/Payments/${from}/${to}`),
	get: id => request.get(`/Trainees/Details/${id}`),
	create: data => request.put('/Trainees/Details', data),
	update: data => request.post(`/Trainees/Details/${data.id}`, data),
	remove: id => request.delete(`/Trainees/Details/${id}`),
	lessons: traineeId => request.get(`/Trainees/${traineeId}/Lessons`),
	updatePhoto: (fileName, data) => request.post(`/Trainees/Photo/${fileName}`, {photo: data}),
	updateNullPhoto: (traineeId, photo) => request.put('/Trainees/Photo', {photo, traineeId}),
	bulkThreatMail: {
		list: () => request.get('/Trainees/BulkThreatMail'),
		send: list => request.post('/Trainees/BulkThreatMail', list)
	},
	settlement: {
		list: traineeId => request.get(`/Trainees/${traineeId}/Settlement`),
		update: (traineeId, entryId, entry) => request.post(`/Trainees/${traineeId}/Settlement/${entryId}`, entry),
		create: (traineeId, entry) => request.put(`/Trainees/${traineeId}/Settlement`, entry),
		remove: (traineeId, entryId, entryType) => request.delete(`/Trainees/${traineeId}/Settlement/${entryId}?entryType=${entryType}`),
		recalculate: traineeId => request.post(`/Trainees/${traineeId}/RecalculateSettlement`),
		paidStatus: (traineeId, status, entries) => request.post(`/Trainees/${traineeId}/SettlementEntriesPayment`, {paid: status, entries}),
		threatMail: traineeId => request.post(`/Trainees/${traineeId}/SendThreatMail`)
	},
	equipment: {
		list: traineeId => request.get(`/Trainees/${traineeId}/Equipment`),
		update: (traineeId, entry) => request.post(`/Trainees/${traineeId}/Equipment/${entry.entryId}`, entry),
		create: (traineeId, entry) => request.put(`/Trainees/${traineeId}/Equipment`, entry),
		remove: (traineeId, entryId) => request.delete(`/Trainees/${traineeId}/Equipment/${entryId}`)
	},
	signature: {
		generateToken: obj => request.put('/Trainees/Signature/Data', obj),
		getData: pin => request.get(`/Trainees/Signature/Data/${pin}`),
		setData: (pin, data) => request.post(`/Trainees/Signature/Data/${pin}`, data),
		uploadPhoto: (fileName, data) => request.post(`/Trainees/Signature/${fileName}`, data)
	}
});

api.$register('signature', {
	getData: pin => request.get(`/Trainees/Signature/Data/${pin}`),
	setData: (pin, data) => request.post(`/Trainees/Signature/Data/${pin}`, data)
});

api.$register('publicPlan', {
	getData: (date = dayjs().format('YYYY-MM-DD')) => request.get(`/V2/Planning/PublicPlan/${date}`)
});

api.$register('system', {
	variables: {
		list: () => request.get('/System/Variables'),
		save: (id, value) => request.post(`/System/Variables/${id}`, {value})
	},
	action: (name, data) => request.post(`/System/Action/${name}`, data),
	traineeRates: {
		list: () => request.get('/System/Trainees/Rates'),
		create: data => request.put('/System/Trainees/Rates', data),
		update: (id, data) => request.post(`/System/Trainees/Rates/${id}`, data),
		remove: id => request.delete(`/System/Trainees/Rates/${id}`),
	},
	versionControl: {
		check: () => request.get('/System/VersionControl'),
		startUpdate: () => request.post('/System/VersionControl'),
		isUpdateInProgress: () => request.get('/System/UpdateProgress')
	}
});
